<template>
  <div>
    <!-- Empty state with drag and drop - shown when no documents and contact has ID -->
    <div
      v-if="(!documents || documents.length === 0) && contact?.id"
      class="flex flex-col items-center justify-center py-12"
    >
      <div
        :class="[
          'border-2 border-dashed rounded-lg p-12 w-full max-w-2xl text-center transition-colors',
          isDragging ? 'border-blue-500 bg-blue-50' : 'border-gray-300 bg-gray-50'
        ]"
        @drop="handleDrop"
        @dragover.prevent="isDragging = true"
        @dragleave="isDragging = false"
        @dragenter.prevent="isDragging = true"
      >
        <i
          :class="[
            'text-6xl mb-4 transition-colors',
            isDragging ? 'pi pi-cloud-upload text-blue-500' : 'pi pi-file text-gray-300'
          ]"
        />
        <h3 class="text-lg font-semibold text-gray-700 mb-2">
          {{ isDragging ? 'Drop files here' : 'Drag and drop files here' }}
        </h3>
        <p class="text-gray-500 mb-4">
          or
        </p>
        <FileUpload
          mode="basic"
          :auto="true"
          choose-label="Browse Files"
          :disabled="isUploading"
          accept=".pdf,.doc,.docx,.xls,.xlsx,.txt,.jpg,.jpeg,.png,.gif,.webp"
          @select="onDocumentUpload"
        />
        <p class="text-sm text-gray-400 mt-4">
          Supported formats: PDF, DOC, DOCX, XLS, XLSX, TXT, Images (max 50MB)
        </p>
      </div>
    </div>

    <!-- Empty state message when contact not saved yet -->
    <div
      v-if="(!documents || documents.length === 0) && !contact?.id"
      class="flex flex-wrap items-center justify-center py-8"
    >
      <div class="text-center">
        <i class="pi pi-file text-5xl text-gray-300 mb-3" />
        <p class="text-gray-500">
          Save the contact first to add documents
        </p>
      </div>
    </div>

    <!-- Card Layout -->
    <div
      v-if="documents && documents.length > 0"
      class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4"
    >
      <Card
        v-for="document in documents"
        :key="document.id"
        class="shadow-sm hover:shadow-md transition-shadow duration-200 cursor-pointer"
        @click="downloadDocument(document)"
      >
        <template #header>
          <div class="flex justify-between items-center bg-gray-50 px-4 py-2">
            <div class="flex items-center">
              <i class="pi pi-file mr-2 text-blue-500" />
              <span class="font-semibold text-sm">{{ document.name }}</span>
            </div>
            <Button
              v-if="isEditing" 
              icon="pi pi-times" 
              severity="danger" 
              size="small"
              rounded 
              outlined
              aria-label="Delete"
              @click.stop="deleteItem(document.id)"
            />
          </div>
        </template>
        <template #content>
          <div class="p-3">
            <div
              v-if="document.document_type"
              class="flex justify-between items-center mb-2"
            >
              <span class="text-gray-600 text-sm">Type:</span>
              <span class="font-medium text-sm">{{ document.document_type }}</span>
            </div>
            <div
              v-if="document.uploaded_at"
              class="flex justify-between items-center mb-2"
            >
              <span class="text-gray-600 text-sm">Uploaded:</span>
              <span class="font-medium text-sm">{{ formatDate(document.uploaded_at) }}</span>
            </div>
            <div
              v-if="document.uploaded_by_name"
              class="flex justify-between items-center mb-2"
            >
              <span class="text-gray-600 text-sm">By:</span>
              <span class="font-medium text-sm">{{ document.uploaded_by_name }}</span>
            </div>
            <div
              v-if="document.file_size"
              class="flex justify-between items-center"
            >
              <span class="text-gray-600 text-sm">Size:</span>
              <span class="font-medium text-sm">{{ formatFileSize(document.file_size) }}</span>
            </div>
          </div>
        </template>
      </Card>
    </div>

    <!-- Upload Dialog -->
    <Dialog
      v-model:visible="showUploadDialog"
      modal
      header="Upload Document"
      :style="{ width: '500px' }"
    >
      <div class="flex flex-col gap-4">
        <div>
          <label class="block text-gray-700 mb-2 font-medium">Document Name</label>
          <InputText
            v-model="uploadDocumentName"
            class="w-full"
            placeholder="Enter document name"
          />
        </div>
        <div>
          <label class="block text-gray-700 mb-2 font-medium">Document Type (Optional)</label>
          <InputText
            v-model="uploadDocumentType"
            class="w-full"
            placeholder="e.g., Contract, Invoice, Legal"
          />
        </div>
      </div>
      <template #footer>
        <Button
          label="Cancel"
          class="p-button-secondary"
          @click="showUploadDialog = false"
        />
        <Button
          label="Upload"
          :disabled="!uploadDocumentName || !uploadDocumentFile"
          @click="confirmDocumentUpload"
        />
      </template>
    </Dialog>
  </div>
</template>

<script setup>
import { ref, computed, defineProps, defineEmits } from 'vue';
import { useToast } from 'primevue/usetoast';
import { useConfirm } from 'primevue/useconfirm';
import { 
    uploadContactDocument, 
    downloadContactDocument, 
    deleteContactDocument 
} from '@/services/contactService';

const props = defineProps({
    contact: {
        type: Object,
        required: true
    },
    isEditing: {
        type: Boolean,
        default: false
    }
});

const emit = defineEmits(['update:contact']);

const toast = useToast();
const confirm = useConfirm();

// Documents from contact
const documents = computed(() => {
    return props.contact?.documents || [];
});

// Upload state
const showUploadDialog = ref(false);
const uploadDocumentFile = ref(null);
const uploadDocumentName = ref('');
const uploadDocumentType = ref('');
const isUploading = ref(false);
const isDragging = ref(false);

// Format date
const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
    });
};

// Format file size
const formatFileSize = (bytes) => {
    if (!bytes) return 'N/A';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
};

// Validate file
const validateFile = (file) => {
    if (!file) return false;
    
    // Validate file size (50MB max)
    if (file.size > 50 * 1024 * 1024) {
        toast.add({
            severity: 'error',
            summary: 'File Too Large',
            detail: 'File must be less than 50MB',
            life: 5000
        });
        return false;
    }
    return true;
};

// Handle document upload selection
const onDocumentUpload = (event) => {
    const file = event.files[0];
    if (!validateFile(file)) return;
    
    uploadDocumentFile.value = file;
    uploadDocumentName.value = file.name;
    showUploadDialog.value = true;
};

// Handle drag and drop
const handleDrop = (event) => {
    isDragging.value = false;
    event.preventDefault();
    
    const files = Array.from(event.dataTransfer.files);
    if (files.length === 0) return;
    
    // Handle first file (can extend to multiple files later)
    const file = files[0];
    if (!validateFile(file)) return;
    
    uploadDocumentFile.value = file;
    uploadDocumentName.value = file.name;
    showUploadDialog.value = true;
};

// Confirm and upload document
const confirmDocumentUpload = async () => {
    if (!uploadDocumentName.value || !uploadDocumentFile.value) return;
    
    isUploading.value = true;
    try {
        const response = await uploadContactDocument(
            props.contact.id,
            uploadDocumentFile.value,
            uploadDocumentName.value,
            uploadDocumentType.value || null
        );
        
        // Update contact with new document
        const updatedContact = {
            ...props.contact,
            documents: [...documents.value, response.data]
        };
        emit('update:contact', updatedContact);
        
        toast.add({
            severity: 'success',
            summary: 'Document Uploaded',
            detail: 'Document uploaded successfully!',
            life: 5000
        });
        
        // Reset form
        showUploadDialog.value = false;
        uploadDocumentFile.value = null;
        uploadDocumentName.value = '';
        uploadDocumentType.value = '';
    } catch (error) {
        console.error('Error uploading document:', error);
        const errorMessage = error.response?.data?.message || 
                            (error.response?.data?.errors ? 
                                Object.values(error.response.data.errors).flat().join(', ') : 
                                'Failed to upload document');
        toast.add({
            severity: 'error',
            summary: 'Upload Failed',
            detail: errorMessage,
            life: 7000
        });
    } finally {
        isUploading.value = false;
    }
};

// Download document
const downloadDocument = async (doc) => {
    try {
        const blob = await downloadContactDocument(props.contact.id, doc.id);
        
        // Create download link
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = doc.name;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        
        toast.add({
            severity: 'success',
            summary: 'Download Started',
            detail: 'Document download started',
            life: 3000
        });
    } catch (error) {
        console.error('Error downloading document:', error);
        toast.add({
            severity: 'error',
            summary: 'Download Failed',
            detail: 'Failed to download document',
            life: 5000
        });
    }
};

// Delete document
const deleteItem = (documentId) => {
    const document = documents.value.find(d => d.id === documentId);
    
    confirm.require({
        message: `Are you sure you want to delete "${document?.name}"? This action cannot be undone.`,
        header: 'Confirm Delete',
        icon: 'pi pi-exclamation-triangle',
        rejectClass: 'p-button-secondary p-button-outlined',
        rejectLabel: 'Cancel',
        acceptLabel: 'Delete',
        accept: async () => {
            try {
                await deleteContactDocument(props.contact.id, documentId);
                
                // Update contact by removing deleted document
                const updatedContact = {
                    ...props.contact,
                    documents: documents.value.filter(d => d.id !== documentId)
                };
                emit('update:contact', updatedContact);
                
                toast.add({
                    severity: 'success',
                    summary: 'Document Deleted',
                    detail: 'Document deleted successfully',
                    life: 5000
                });
            } catch (error) {
                console.error('Error deleting document:', error);
                const errorMessage = error.response?.data?.message || 'Failed to delete document';
                toast.add({
                    severity: 'error',
                    summary: 'Delete Failed',
                    detail: errorMessage,
                    life: 7000
                });
            }
        }
    });
};
</script> 